<p-toast></p-toast>
<p-confirmDialog [key]="confirmKey"></p-confirmDialog>

<div class="flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
  <div class="flex flex-wrap gap-3">
    <div class="flex flex-column gap-1">
      <label for="remunerationGridYear" class="font-semibold">{{ 'remunerationGrid.year' | translate }}</label>
      <p-dropdown
        inputId="remunerationGridYear"
        [options]="years"
        [(ngModel)]="selectedYear"
        (onChange)="onYearChange()"
        [disabled]="loading || saving"
        styleClass="w-10rem"
      ></p-dropdown>
    </div>
    <div *ngIf="!isPracticeScoped" class="flex flex-column gap-1">
      <label for="remunerationGridPractice" class="font-semibold">{{ 'remunerationGrid.practice' | translate }}</label>
      <p-dropdown
        inputId="remunerationGridPractice"
        [options]="practices"
        [(ngModel)]="selectedPractice"
        (onChange)="onPracticeChange()"
        [disabled]="loading || saving"
        styleClass="w-12rem"
      ></p-dropdown>
    </div>
  </div>

  <div *ngIf="canWrite" class="flex gap-2">
    <p-button
      data-testid="grid-cancel"
      [label]="'remunerationGrid.cancel' | translate"
      severity="secondary"
      [outlined]="true"
      [disabled]="!isDirty || saving || loading"
      (onClick)="cancelChanges()"
    ></p-button>
    <p-button
      data-testid="grid-save"
      [label]="'remunerationGrid.save' | translate"
      icon="pi pi-check"
      [loading]="saving"
      [disabled]="!canSave"
      (onClick)="save()"
    ></p-button>
  </div>
</div>

<p *ngIf="practicesLoaded && !loading && practices.length === 0" data-testid="grid-no-selection" class="grid-notice">
  <i class="pi pi-info-circle mr-2"></i>{{ 'remunerationGrid.noSelection' | translate }}
</p>
<p *ngIf="grid && !grid.saved && grid.copiedFromYear" data-testid="grid-copied-notice" class="grid-notice">
  <i class="pi pi-info-circle mr-2"></i>{{ 'remunerationGrid.copiedNotice' | translate: { year: grid.copiedFromYear } }}
</p>
<p *ngIf="grid && !grid.saved && !grid.copiedFromYear" data-testid="grid-empty-notice" class="grid-notice">
  <i class="pi pi-info-circle mr-2"></i>{{ 'remunerationGrid.emptyNotice' | translate }}
</p>

<div class="grid">
  <div *ngFor="let table of tables" class="col-12 xl:col-6">
    <div class="grid-table-title mb-2">
      <h3 class="text-lg font-semibold m-0">{{ table.titleKey | translate }}</h3>
      <!-- Parent switch: sets every category of the table; MIXED when the categories differ. -->
      <div class="increase-type-toggle" [attr.data-testid]="table.testId + '-toggle'">
        <span class="increase-type-label">{{ table.typeLabelKey | translate }}:</span>
        <span [class.active-unit]="tableType(table) === 'AMOUNT'">{{ 'remunerationGrid.amount' | translate }}</span>
        <p-inputSwitch
          [inputId]="table.testId"
          [ngModel]="tableType(table) === 'PERCENTAGE'"
          (ngModelChange)="setTableType(table, $event)"
          [class.mixed-switch]="tableType(table) === 'MIXED'"
          [disabled]="!canEdit || rows.length === 0"
        ></p-inputSwitch>
        <span [class.active-unit]="tableType(table) === 'PERCENTAGE'">{{ 'remunerationGrid.percentage' | translate }}</span>
        <span *ngIf="tableType(table) === 'MIXED'" class="mixed-badge" [attr.data-testid]="table.testId + '-mixed'">
          {{ 'remunerationGrid.mixed' | translate }}
        </span>
      </div>
    </div>
    <p-table [value]="rows" [loading]="loading || saving" dataKey="categoryShortLabel" styleClass="p-datatable-sm p-datatable-gridlines">
      <ng-template pTemplate="header">
        <tr>
          <th>{{ 'remunerationGrid.category' | translate }}</th>
          <th class="text-center">{{ 'remunerationGrid.type' | translate }}</th>
          <th *ngFor="let n of noteIndexes" class="text-center">{{ n + 1 }}</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
        <tr>
          <td class="font-semibold">{{ row.categoryShortLabel }}</td>
          <td class="text-center" [class.grid-cell-changed]="isTypeChanged(rowIndex, table)">
            <div class="row-type-toggle" [attr.data-testid]="table.testId + '-row-' + row.categoryShortLabel">
              <span [class.active-unit]="!isPercentRow(row, table)">DH</span>
              <p-inputSwitch
                styleClass="row-type-switch"
                [ngModel]="isPercentRow(row, table)"
                (ngModelChange)="setRowType(row, table, $event)"
                [disabled]="!canEdit"
              ></p-inputSwitch>
              <span [class.active-unit]="isPercentRow(row, table)">%</span>
            </div>
          </td>
          <ng-container *ngFor="let n of noteIndexes">
            <td
              *ngIf="canEdit; else readOnlyCell"
              pEditableColumn
              class="text-center"
              [class.grid-cell-changed]="isChanged(rowIndex, table.field, n)"
            >
              <p-cellEditor>
                <ng-template pTemplate="input">
                  <p-inputNumber
                    [ngModel]="row[table.field][n]"
                    (ngModelChange)="onCellChange(row, table.field, n, $event)"
                    [min]="0"
                    [max]="cellMax(row, table)"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="2"
                    inputStyleClass="w-full text-center"
                  ></p-inputNumber>
                </ng-template>
                <ng-template pTemplate="output">{{ row[table.field][n] | number: '1.0-2' }}{{ cellSuffix(row, table) }}</ng-template>
              </p-cellEditor>
            </td>
            <ng-template #readOnlyCell>
              <td class="text-center">{{ row[table.field][n] | number: '1.0-2' }}{{ cellSuffix(row, table) }}</td>
            </ng-template>
          </ng-container>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="7" class="text-center text-600">{{ grid ? ('remunerationGrid.noCategories' | translate) : '' }}</td>
        </tr>
      </ng-template>
    </p-table>
  </div>
</div>

<p data-testid="grid-unit-note" class="grid-unit-note">
  <i class="pi pi-exclamation-circle mr-2"></i>
  {{ 'remunerationGrid.unitsNote' | translate }}
</p>
<p *ngIf="hasInvalidPercentageValues" data-testid="grid-percentage-error" class="grid-unit-note text-red-500">
  <i class="pi pi-times-circle mr-2"></i>{{ 'remunerationGrid.percentageRangeError' | translate }}
</p>
